import React, { useState } from 'react';
import { ChevronDown, HelpCircle } from 'lucide-react';

const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      question: "What do students need for online lessons?",
      answer: "Students need a computer/tablet with a camera and microphone, stable internet connection, and basic stationery (pen, paper, calculator). We use Zoom for lessons - we'll send you the link before each session."
    },
    {
      question: "How do online group lessons work?",
      answer: "Group lessons have 4-6 students of similar ability. Dr. Michael teaches interactively, asking questions and working through problems together. Students can ask questions anytime via chat or voice. All sessions are recorded for revision."
    },
    {
      question: "What's the difference between Foundation and Higher tier?",
      answer: "Foundation tier covers grades 1-5, while Higher tier covers grades 4-9. We assess each student and recommend the appropriate tier. Some students move between tiers as they progress."
    },
    {
      question: "Can I cancel or reschedule a lesson?",
      answer: "Yes! Cancel or reschedule with at least 24 hours notice for a full refund/credit. Late cancellations or no-shows are charged in full. We understand emergencies happen - just let us know."
    },
    {
      question: "How quickly will my child see improvement?",
      answer: "Most students see noticeable improvement within 4-6 weeks of consistent attendance. However, this varies based on starting level, how much practice they do between sessions, and exam proximity."
    },
    {
      question: "Do you offer 1:1 tuition?",
      answer: "Yes! Our VIP 1:1 sessions are £99.99/hour and include all GCSE Sciences (Maths, Chemistry, Physics, Biology). These are fully personalised to your child's specific needs and exam board."
    },
    {
      question: "What exam boards do you cover?",
      answer: "We cover all major exam boards: AQA, Edexcel, OCR, WJEC, and IGCSE (Cambridge & Edexcel). Dr. Michael is an experienced IGCSE examiner, giving students insider exam technique tips."
    },
    {
      question: "Is there homework between sessions?",
      answer: "We provide optional practice questions after each session. Students who complete homework typically improve faster. We also share resources, past papers, and revision materials."
    },
    {
      question: "How do I track my child's progress?",
      answer: "We provide regular feedback after sessions and can arrange parent progress calls. For monthly package students, we provide detailed monthly progress reports."
    },
    {
      question: "What's your refund policy?",
      answer: "One-off lessons: Full refund if cancelled 24+ hours before. Monthly packages: Unused sessions can be carried over for one month or refunded pro-rata if you cancel the subscription."
    }
  ];

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section id="faq" style={{ 
      padding: '5rem 1.5rem',
      background: 'linear-gradient(180deg, #F8FAFC 0%, #F1F5F9 100%)'
    }}>
      <div className="container" style={{ maxWidth: '900px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem',
            background: 'rgba(13, 148, 136, 0.1)',
            padding: '0.5rem 1rem',
            borderRadius: '9999px',
            marginBottom: '1rem'
          }}>
            <HelpCircle size={18} color="#C5A059" />
            <span style={{ color: '#C5A059', fontWeight: 600, fontSize: '0.9rem' }}>Got Questions?</span>
          </div>
          <h2 className="heading-2" style={{ marginBottom: '1rem' }}>
            Frequently Asked Questions
          </h2>
          <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '600px', margin: '0 auto' }}>
            Everything you need to know about our GCSE Maths tuition
          </p>
        </div>

        {/* FAQ Accordion */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {faqs.map((faq, index) => (
            <div
              key={index}
              style={{
                background: 'white',
                borderRadius: '12px',
                overflow: 'hidden',
                boxShadow: openIndex === index 
                  ? '0 4px 20px rgba(13, 148, 136, 0.15)' 
                  : '0 2px 8px rgba(0, 0, 0, 0.05)',
                border: openIndex === index 
                  ? '2px solid #C5A059' 
                  : '2px solid transparent',
                transition: 'all 0.3s ease'
              }}
            >
              <button
                onClick={() => toggleFAQ(index)}
                style={{
                  width: '100%',
                  padding: '1.25rem 1.5rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  textAlign: 'left'
                }}
                data-testid={`faq-question-${index}`}
              >
                <span style={{ 
                  fontWeight: 600, 
                  color: '#0F172A',
                  fontSize: '1rem',
                  paddingRight: '1rem'
                }}>
                  {faq.question}
                </span>
                <ChevronDown 
                  size={20} 
                  color="#C5A059"
                  style={{
                    transform: openIndex === index ? 'rotate(180deg)' : 'rotate(0)',
                    transition: 'transform 0.3s ease',
                    flexShrink: 0
                  }}
                />
              </button>
              
              <div
                style={{
                  maxHeight: openIndex === index ? '500px' : '0',
                  overflow: 'hidden',
                  transition: 'max-height 0.3s ease'
                }}
              >
                <div style={{ 
                  padding: '0 1.5rem 1.25rem 1.5rem',
                  color: '#64748B',
                  fontSize: '0.95rem',
                  lineHeight: 1.7
                }}>
                  {faq.answer}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Still have questions CTA */}
        <div style={{
          marginTop: '3rem',
          textAlign: 'center',
          padding: '2rem',
          background: 'white',
          borderRadius: '16px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)'
        }}>
          <p style={{ color: '#374151', fontSize: '1.1rem', marginBottom: '1rem' }}>
            Still have questions? We're here to help!
          </p>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <a
              href="https://wa.me/447948213565?text=Hi!%20I%20have%20a%20question%20about%20GCSE%20Maths%20tuition."
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                background: '#25D366',
                color: 'white',
                padding: '0.75rem 1.5rem',
                borderRadius: '9999px',
                textDecoration: 'none',
                fontWeight: 600,
                fontSize: '0.9rem'
              }}
            >
              💬 WhatsApp Us
            </a>
            <a
              href="https://calendly.com/drmomakobia/studentcall"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                background: '#C5A059',
                color: 'white',
                padding: '0.75rem 1.5rem',
                borderRadius: '9999px',
                textDecoration: 'none',
                fontWeight: 600,
                fontSize: '0.9rem'
              }}
            >
              📞 Book a Call
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
